import DropdownControlWithChevron from '@/features/ui/inputs/components/DropdownControl/WithChevron'
import { OptionProps } from '@/features/ui/inputs/components/Select/parts/Option'
import {
    Children,
    FC,
    ReactElement,
    ReactNode,
    cloneElement,
    isValidElement,
    useCallback,
    useState,
} from 'react'
import { StyleProp, View, ViewStyle } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'

interface SelectProps {
    children: ReactElement<OptionProps> | ReactElement<OptionProps>[]
    disabled?: boolean
    label: string
    value: string | null
    onValueChange: (value: string) => void
    placeholder: string
    alignFrontIconToLabel?: boolean
    FrontIcon?: ReactNode
    TailIcon?: ReactNode
    ActiveTailIcon?: ReactNode
    containerStyle?: StyleProp<ViewStyle>
    style?: StyleProp<ViewStyle>
}

const Select: FC<SelectProps> = ({
    children,
    disabled,
    label,
    value,
    onValueChange,
    placeholder,
    alignFrontIconToLabel,
    FrontIcon,
    containerStyle,
    style,
}) => {
    const [open, setOpen] = useState(false)

    const handleSelect = (value: string) => {
        onValueChange(value)
        setOpen(false)
    }

    const onToggleOpen = useCallback(() => setOpen((open) => !open), [])

    const selectedOption = Children.toArray(children).find(
        (child): child is ReactElement<OptionProps> => {
            if (!isValidElement<OptionProps>(child)) return false

            return child.props.value === value
        }
    )

    const selectedOptionText = selectedOption?.props.children

    const renderedChildren = Children.map(children, (child) => {
        if (!isValidElement<OptionProps>(child)) return child

        if ((child.type as any).displayName !== 'Select.Option') return child

        return cloneElement(child, {
            onPress: () => handleSelect(child.props.value),
        })
    })

    return (
        <DropdownControlWithChevron
            open={open}
            disabled={disabled}
            label={label}
            value={selectedOptionText || ''}
            placeholder={placeholder}
            onToggleOpen={onToggleOpen}
            alignFrontIconToLabel={alignFrontIconToLabel}
            FrontIcon={FrontIcon}
            containerStyle={containerStyle}
            style={style}
        >
            <View style={{ borderRadius: 8, overflow: 'hidden' }}>
                <ScrollView
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={{ paddingVertical: 8 }}
                    style={{ maxHeight: 200 }}
                >
                    {renderedChildren}
                </ScrollView>
            </View>
        </DropdownControlWithChevron>
    )
}

export default Select
